import {
  IAccount,
  IAccountService,
  IProperty,
  IPropertySelection,
  IUserContext,
} from "@origin-digital/event-dispatcher";
import { ServiceType } from "@origin-digital/event-dispatcher/src/userContext.types";
import { mapToAccountServices } from "./mapAccountServices";

interface PropertyAccounts {
  property: IProperty;
  accounts: IAccount[];
}

const getPropertyAccountGroups = (accounts: IAccount[]): PropertyAccounts[] => {
  const propertyIdToGroup: Record<string, PropertyAccounts> = {};
  accounts.forEach((account) => {
    account.services.forEach((service) => {
      if (!propertyIdToGroup[service.property.id]) {
        propertyIdToGroup[service.property.id] = {
          property: service.property,
          accounts: [],
        };
      }
      if (!propertyIdToGroup[service.property.id].accounts.includes(account)) {
        propertyIdToGroup[service.property.id].accounts.push(account);
      }
    });
  });
  return Object.values(propertyIdToGroup);
};

const SERVICE_TYPE_ORDER: Record<ServiceType, number> = {
  ELECTRICITY: 0,
  NATURAL_GAS: 1,
  HOT_WATER: 2,
  BROADBAND: 3,
  LPG: 4,
  OHM: 5,
  VPP_BATTERY: 6,
  UNKNOWN: 7,
};

/**
 * Sort properties by...
 * 1. postcode (ascending)
 * 2. suburb (ascending)
 * 3. primary service type (ELECTRICITY, NATURAL_GAS, HOT_WATER, BROADBAND, LPG, OHM, VPP_BATTERY, UNKNOWN)
 * 4. primary accountId (descending)
 */
const sortProperties = (
  a: IPropertySelection,
  b: IPropertySelection
): number => {
  if (a.address.postcode !== b.address.postcode) {
    return (a.address.postcode ?? "").localeCompare(b.address.postcode ?? "");
  }
  if (a.address.suburb !== b.address.suburb) {
    return (a.address.suburb ?? "").localeCompare(b.address.suburb ?? "");
  }
  const aPrimaryAccount = a.accountServices[0];
  const bPrimaryAccount = b.accountServices[0];
  if (
    SERVICE_TYPE_ORDER[aPrimaryAccount.serviceType] !==
    SERVICE_TYPE_ORDER[bPrimaryAccount.serviceType]
  ) {
    return (
      SERVICE_TYPE_ORDER[aPrimaryAccount.serviceType] -
      SERVICE_TYPE_ORDER[bPrimaryAccount.serviceType]
    );
  }
  // reverse accountId order
  return bPrimaryAccount.accountId.localeCompare(aPrimaryAccount.accountId);
};

/**
 * Create a list of possible property selections for the given user data.
 * Each property is a grouping containing a single property and a collection of account-service combinations for that property.
 */
export const mapUserDataToProperties = (
  userContext: IUserContext | undefined,
  multipleServicesEnabled: boolean
): IPropertySelection[] => {
  if (!userContext) {
    return [];
  }

  const propertyAccountGroups = getPropertyAccountGroups(userContext.accounts);

  return propertyAccountGroups
    .map(({ property, accounts }) => {
      const accountServices: IAccountService[] = mapToAccountServices(
        accounts,
        property.id,
        multipleServicesEnabled
      );
      return {
        ...property,
        accountServices,
        selectedAccountService: accountServices[0],
        isPrevious: accountServices.every(
          (accountService) => accountService.isClosed
        ),
      };
    })
    .filter((property) => property.accountServices.length)
    .sort(sortProperties);
};
